<template>
  <button
    v-show="visible"
    type="button"
    class="scroll-hint"
    aria-label="More content below"
    title="Scroll for more"
    @click="scrollForMore"
  >
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        stroke-width="3.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 9l6 6 6-6"
      />
    </svg>
  </button>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import tinycolor from "tinycolor2";

const SCROLL_HINT_COLOR_PARAM = "scroll_hint_color";
const DEFAULT_PRIMARY_COLOR = "#000000";

const props = defineProps<{
  /** Active theme primary button background color. */
  primaryButtonColor?: string;
}>();

const visible = ref(false);

let resizeObserver: ResizeObserver | undefined;
let mutationObserver: MutationObserver | undefined;
let scrollRoot: Element | null = null;
let observedView: Element | null = null;
let rafId = 0;

function getQueryScrollHintColor(): string | null {
  const value = new URLSearchParams(window.location.search).get(
    SCROLL_HINT_COLOR_PARAM,
  );
  if (!value) {
    return null;
  }
  // Allow any CSS color; reject empty / whitespace-only values.
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

const queryScrollHintColor = getQueryScrollHintColor();

const backgroundColor = computed(() => {
  return (
    queryScrollHintColor || props.primaryButtonColor || DEFAULT_PRIMARY_COLOR
  );
});

const iconColor = computed(() => {
  const color = tinycolor(backgroundColor.value);
  return color.isLight() ? "black" : "white";
});

function getScrollRoot(): Element {
  return (
    document.querySelector(".app-shell") ||
    document.scrollingElement ||
    document.documentElement
  );
}

function updateVisibility() {
  const root = scrollRoot || getScrollRoot();
  const { scrollTop, clientHeight, scrollHeight } = root;
  const hasOverflow = scrollHeight > clientHeight + 1;
  const atBottom = scrollTop + clientHeight >= scrollHeight - 2;
  visible.value = hasOverflow && !atBottom;
}

/** Wait for layout after DOM/size changes, then re-check overflow. */
function scheduleUpdateVisibility() {
  cancelAnimationFrame(rafId);
  rafId = requestAnimationFrame(() => {
    ensureViewObserved();
    updateVisibility();
  });
}

function ensureViewObserved() {
  if (!resizeObserver) {
    return;
  }
  const viewElement = document.querySelector(".view");
  if (viewElement && viewElement !== observedView) {
    if (observedView) {
      resizeObserver.unobserve(observedView);
    }
    observedView = viewElement;
    resizeObserver.observe(viewElement);
  }
}

function scrollForMore() {
  const root = scrollRoot || getScrollRoot();
  root.scrollBy({
    top: Math.round(root.clientHeight * 0.75),
    behavior: "smooth",
  });
}

onMounted(() => {
  scrollRoot = getScrollRoot();
  scrollRoot.addEventListener("scroll", updateVisibility, { passive: true });
  window.addEventListener("resize", updateVisibility);
  window.addEventListener("load", scheduleUpdateVisibility);

  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(() => {
      scheduleUpdateVisibility();
    });
    // Shell's border-box often stays fixed; .view size changes when content loads.
    ensureViewObserved();
  }

  if (typeof MutationObserver !== "undefined") {
    mutationObserver = new MutationObserver(() => {
      scheduleUpdateVisibility();
    });
    mutationObserver.observe(scrollRoot, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  scheduleUpdateVisibility();
});

onUnmounted(() => {
  cancelAnimationFrame(rafId);
  scrollRoot?.removeEventListener("scroll", updateVisibility);
  window.removeEventListener("resize", updateVisibility);
  window.removeEventListener("load", scheduleUpdateVisibility);
  resizeObserver?.disconnect();
  mutationObserver?.disconnect();
});
</script>

<style scoped>
.scroll-hint {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 2px solid #fff;
  border-radius: 50%;
  background: v-bind(backgroundColor);
  color: v-bind(iconColor);
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  animation: scroll-hint-jump 0.9s ease-in-out infinite;
}

.scroll-hint svg {
  width: 18px;
  height: 18px;
  display: block;
}

@keyframes scroll-hint-jump {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(10px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .scroll-hint {
    animation: none;
  }
}
</style>
